import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/AppShell";
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Chat from "@/pages/Chat";
import Documents from "@/pages/Documents";
import Highlights from "@/pages/Highlights";
import Readiness from "@/pages/Readiness";
import Dashboard from "@/pages/Dashboard";
import BestPractices from "@/pages/BestPractices";
import Forms from "@/pages/Forms";
import FormFill from "@/pages/FormFill";
import SubmissionView from "@/pages/SubmissionView";
import FormPrint from "@/pages/FormPrint";

import Legal from "@/pages/Legal";
import ConsentGate from "@/components/ConsentGate";
import Audit from "@/pages/Audit";
import Verify from "@/pages/Verify";
import Onboarding from "@/pages/Onboarding";
import PracticeCheck from "@/pages/PracticeCheck";
import ActionCenter from "@/pages/ActionCenter";
import Team from "@/pages/Team";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import EmployeeCenter from "@/pages/EmployeeCenter";
import VerifyEmail, { VerifyGate } from "@/pages/VerifyEmail";

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (!user.terms_accepted) return <ConsentGate />;
  if (user.email_verified === false) return <VerifyGate />;
  return <AppShell><Outlet /></AppShell>;
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth return: process #session_id before any protected route runs
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/terms" element={<Legal />} />
      <Route path="/privacy" element={<Legal />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/app" element={<Protected />}>
        <Route index element={<Navigate to="/app/chat" replace />} />
        <Route path="chat" element={<Chat />} />
        <Route path="documents" element={<Documents />} />
        <Route path="practices" element={<BestPractices />} />
        <Route path="forms" element={<Forms />} />
        <Route path="forms/fill/:templateId" element={<FormFill />} />
        <Route path="forms/print/:templateId" element={<FormPrint />} />
        <Route path="forms/submissions/:submissionId" element={<SubmissionView />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="highlights" element={<Highlights />} />
        <Route path="readiness" element={<Readiness />} />
        <Route path="readiness/practice-check" element={<PracticeCheck />} />
        <Route path="audit" element={<Audit />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="action-center" element={<ActionCenter />} />
        <Route path="team" element={<Team />} />
        <Route path="employee-center" element={<EmployeeCenter />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
